/**
 * User-message text renderer that turns inline `:type[label]{name=id}`
 * directives back into chips. Three mention types render here:
 *  - `surface` (canvas items, triggered by `#`) — neutral chip.
 *  - `agent`   (delegation targets, triggered by `@`) — teal chip with Bot
 *    icon, matching the subagent visual language used elsewhere
 *    (`DelegationLinkChip`, subagent CoT icons).
 *  - `file`    (VFS files, triggered by `!`) — neutral chip with Paperclip
 *    icon. Chip text is the filename; full VFS path is on the `title`
 *    attribute so hovering reveals where the file lives.
 *
 * The Lexical-based composer serializes mentions via the assistant-ui default
 * directive formatter into `:type[label]{name=id}` syntax in the runtime's
 * stored message text. The default `MarkdownText` renderer treats that as
 * plain text and shows the raw markup; we parse the text into segments and
 * render mentions as styled chips inline, keeping the rest as plain text
 * with newline preservation.
 *
 * We intentionally don't run markdown on user messages — users almost never
 * write structured content into the prompt, and splitting markdown across
 * mention boundaries (e.g., `**bold @mention bold**`) would silently break
 * formatting context. If that becomes a real need, parse each text segment
 * with MarkdownText individually.
 */

import { Fragment, useMemo } from 'react';
import { unstable_defaultDirectiveFormatter } from '@assistant-ui/core';
import { Bot, Paperclip } from 'lucide-react';
import {
  MENTION_TYPE_AGENT,
  MENTION_TYPE_FILE,
  MENTION_TYPE_SURFACE,
} from '../../utils/directive-mentions';

interface MentionAwareUserTextProps {
  text: string;
}

// Reloaded messages carry the prose form that `extractMentions` sends to the
// backend (`!filename (path)`, or `!filename (path in knowledge base kbs-…)`
// for KB files), not the original `:file[…]{name=…}` directive. Re-inflate so
// the formatter parses it as a chip instead of plain text, re-encoding the KB
// id back into the directive id (`${kbId}::${path}`) so `parseFileRefId` can
// recover it — mirrors `buildFileRefId` in directive-mentions.ts.
const FILE_MENTION_PROSE =
  /!([^\s()]+) \(([^()]+?)(?: in knowledge base (kbs-[^()]+))?\)/g;
// Exported for the round-trip unit test (it must invert the prose form
// `extractMentions` emits — see tests/unit/utils/directive-mentions.test.ts).
export function reHydrateFileMentions(text: string): string {
  return text.replace(
    FILE_MENTION_PROSE,
    (_, name: string, path: string, kbId: string | undefined) =>
      `:file[${name}]{name=${kbId ? `${kbId}::${path}` : path}}`,
  );
}

export function MentionAwareUserText({ text }: MentionAwareUserTextProps) {
  const segments = useMemo(
    () => unstable_defaultDirectiveFormatter.parse(reHydrateFileMentions(text)),
    [text],
  );

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.kind === 'text') {
          // Split on \n so plain text keeps line breaks the user typed.
          const lines = seg.text.split('\n');
          return (
            <Fragment key={i}>
              {lines.map((line, j) => (
                <Fragment key={j}>
                  {line}
                  {j < lines.length - 1 && <br />}
                </Fragment>
              ))}
            </Fragment>
          );
        }
        if (seg.type === MENTION_TYPE_AGENT) {
          return (
            <span
              key={i}
              className="mx-0.5 inline-flex items-center gap-1 rounded-[14px] bg-accent-subagent-fg/15 px-1.5 py-0.5 text-sm font-medium text-accent-subagent-fg"
            >
              <Bot className="h-3 w-3" />
              {seg.label}
            </span>
          );
        }
        if (seg.type === MENTION_TYPE_SURFACE) {
          return (
            <span
              key={i}
              className="mx-0.5 inline-flex items-center rounded-[14px] bg-foreground/10 px-1.5 py-0.5 text-sm font-medium text-foreground"
            >
              {seg.label}
            </span>
          );
        }
        if (seg.type === MENTION_TYPE_FILE) {
          // Filename in the chip; full VFS path revealed on hover. Same
          // chrome as the surface chip with a paperclip glyph to signal
          // "this is a file reference".
          return (
            <span
              key={i}
              title={seg.id}
              className="mx-0.5 inline-flex items-center gap-1 rounded-[14px] bg-foreground/10 px-1.5 py-0.5 text-sm font-medium text-foreground"
            >
              <Paperclip className="h-3 w-3" />
              {seg.label}
            </span>
          );
        }
        // Unknown directive — render the label as plain text so the bubble
        // never shows raw `:type[…]{…}` markup.
        return <Fragment key={i}>{seg.label}</Fragment>;
      })}
    </>
  );
}
